import { useState, useEffect, useMemo, useCallback } from 'react';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { DetailShell, Section, Icon } from './detail/DetailShell';
import { SearchableSelect } from './ui/SearchableSelect';
import { usePageActions } from '../contexts/PermissionContext';

// KAMPANYA YETKİLERİ (menü 9120) — temsilcilere iskonto / fiyat / kampanya yetkisi.
//
// Tek sayfa: kriter seçilir (Tek Temsilci · Temsilci Grubu · Temsilci Tipi), 5 sekmedeki detaylar
// için mod verilir, tek yerden kaydedilir. Kayıt hedef bazında TAMAMEN yeniden yazılır
// (full-replace) — Mobil Yetki Profilleri'ndeki `PUT /Rights` ile aynı desen.
const API = '/api/campaign-authorizations';

// Hedef eksenleri. Sayısal değer aynı zamanda ÖZGÜLLÜK sırasıdır (büyük olan daha özel) —
// belge kesilirken Temsilci(3) > Grup(2) > Tip(1) kazanır.
const TARGET = { TYPE: 1, GROUP: 2, REP: 3 };
const CRITERIA = [
  { key: TARGET.REP, label: 'Tek Temsilci', icon: 'user', hint: 'Bir kişiye özel istisna' },
  { key: TARGET.GROUP, label: 'Temsilci Grubu', icon: 'users', hint: 'Grup politikası' },
  { key: TARGET.TYPE, label: 'Temsilci Tipi', icon: 'layers', hint: 'Tip tabanı' },
];

// Üç mod. Sıra ekranda soldan sağa aynen görünür.
const MODE = { NONE: 0, LIMITED: 1, FULL: 2 };
const MODES = [
  { key: MODE.NONE, label: 'Yetki Yok', tone: 'none' },
  { key: MODE.LIMITED, label: 'Sınırlı', tone: 'limited' },
  { key: MODE.FULL, label: 'Tam Yetki', tone: 'full' },
];

// Üst sınır birimleri (backend AuthorizationCapType).
const CAP = { PERCENT: 1, AMOUNT: 2, QUANTITY: 3 };
const CAP_SUFFIX = { [CAP.PERCENT]: '%', [CAP.AMOUNT]: '₺', [CAP.QUANTITY]: 'adet' };

// Üst sınırın sayıldığı kapsam — yalnız Kampanya sekmesinde anlamlı.
const SCOPE = { DOCUMENT: 0, CAMPAIGN: 1 };

const TAB_ICONS = {
  kalem_iskonto: 'list',
  kalem_tutar: 'wallet',
  belge_alti: 'layers',
  fiyat: 'tag',
  kampanya: 'box',
};

function unwrap(p) { return p?.data ?? p?.Data ?? p; }

export function CampaignAuthorizationsPageLegacy() {
  usePageMeta('Kampanya Yetkileri', 'Satış temsilcilerine iskonto, fiyat ve kampanya yetkisi tanımlayın.');
  const { canEdit } = usePageActions();

  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState(null);

  const [targetType, setTargetType] = useState(TARGET.REP);
  const [targetId, setTargetId] = useState('');
  const [targetLabel, setTargetLabel] = useState('');
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);

  // detailKey -> { mode, capType, capValue, capScope }
  const [rules, setRules] = useState({});
  const [configured, setConfigured] = useState(false);   // hedefin kayıtlı en az bir kuralı var mı
  const [loading, setLoading] = useState(true);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };

  const details = useMemo(() => catalog.flatMap((t) => t.details || []), [catalog]);
  const detailByKey = useMemo(
    () => Object.fromEntries(details.map((d) => [d.key, d])),
    [details],
  );

  // ---- Katalog + grup/tip listeleri (bir kez) ----
  useEffect(() => {
    (async () => {
      try {
        const [catRes, grpRes, typRes] = await Promise.all([
          fetch(`${API}/Catalog`, { headers: authHeaders() }),
          fetch(`${API}/Targets?targetType=${TARGET.GROUP}`, { headers: authHeaders() }),
          fetch(`${API}/Targets?targetType=${TARGET.TYPE}`, { headers: authHeaders() }),
        ]);
        const cat = unwrap(await catRes.json()) || [];
        setCatalog(cat);
        setTab(cat[0]?.key ?? null);
        setGroups(unwrap(await grpRes.json()) || []);
        setTypes(unwrap(await typRes.json()) || []);
      } catch {
        showToast('error', 'Katalog yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---- Seçili hedefin kuralları ----
  const loadTarget = useCallback(async (type, id) => {
    if (!id) { setRules({}); setConfigured(false); return; }
    setLoadingTarget(true);
    try {
      const res = await fetch(`${API}?targetType=${type}&targetId=${id}`, { headers: authHeaders() });
      const body = unwrap(await res.json());
      const next = {};
      (body?.items || []).forEach((it) => {
        next[it.detailKey] = {
          mode: it.mode,
          capType: it.capType ?? null,
          capValue: it.capValue ?? null,
          capScope: it.capScope ?? SCOPE.DOCUMENT,
        };
      });
      setRules(next);
      setConfigured(Object.keys(next).length > 0);
    } catch {
      showToast('error', 'Yetkiler yüklenemedi');
    } finally {
      setLoadingTarget(false);
    }
  }, []);

  useEffect(() => { loadTarget(targetType, targetId); }, [targetType, targetId, loadTarget]);

  const pickCriterion = (key) => {
    if (key === targetType) return;
    setTargetType(key);
    setTargetId('');
    setTargetLabel('');
    setRules({});
    setConfigured(false);
  };

  // Temsilci araması sunucu taraflıdır — binlerce temsilcide tüm listeyi indirmek anlamsız.
  const searchReps = useCallback(async (q) => {
    const res = await fetch(`${API}/Targets?targetType=${TARGET.REP}&q=${encodeURIComponent(q || '')}`, { headers: authHeaders() });
    const list = unwrap(await res.json()) || [];
    return list.map((r) => ({ value: r.id, label: r.code ? `${r.name} · ${r.code}` : r.name }));
  }, []);

  // ---- Satır durumu ----
  // Kayıt olmayan detay "Yetki Yok"tur (güvenli taban) — bu yüzden okuma tarafında da varsayılan None.
  const ruleOf = (key) => rules[key] ?? { mode: MODE.NONE, capType: null, capValue: null, capScope: SCOPE.DOCUMENT };

  const setMode = (key, mode) => {
    const def = detailByKey[key];
    setRules((cur) => {
      const prev = cur[key] ?? {};
      if (mode !== MODE.LIMITED) {
        return { ...cur, [key]: { mode, capType: null, capValue: null, capScope: SCOPE.DOCUMENT } };
      }
      // Sınırlı'ya geçince birim seçili değilse ilk izinli birimi al; değer boşsa kullanıcı doldursun.
      const capType = prev.capType ?? def?.allowedCapTypes?.[0] ?? CAP.PERCENT;
      return {
        ...cur,
        [key]: { mode, capType, capValue: prev.capValue ?? '', capScope: prev.capScope ?? SCOPE.DOCUMENT },
      };
    });
  };

  const patch = (key, fields) => setRules((cur) => ({ ...cur, [key]: { ...ruleOf(key), ...fields } }));

  // ---- Kaydet ----
  const persist = async () => {
    if (!targetId) { showToast('error', 'Önce bir yetki hedefi seçin'); return; }

    // Sınırlı için üst sınır > 0 zorunlu — sınırsız bir "Sınırlı", Tam Yetki'nin yanlış adıdır.
    const missing = Object.entries(rules).filter(([, r]) =>
      r.mode === MODE.LIMITED && !(Number(r.capValue) > 0));
    if (missing.length > 0) {
      const names = missing.map(([k]) => detailByKey[k]?.name || k).join(', ');
      showToast('error', `Sınırlı seçilen şu detaylarda üst sınır girilmeli: ${names}`);
      return;
    }

    setSaving(true);
    try {
      const items = Object.entries(rules)
        .filter(([key, r]) => detailByKey[key] && r.mode !== MODE.NONE)
        .map(([key, r]) => ({
          tab: detailByKey[key].tab ?? undefined,
          detailKey: key,
          mode: r.mode,
          capType: r.mode === MODE.LIMITED ? r.capType : null,
          capValue: r.mode === MODE.LIMITED ? Number(r.capValue) : null,
          capScope: r.mode === MODE.LIMITED ? r.capScope : null,
        }));

      const res = await fetch(API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ targetType, targetId, items }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Yetkiler kaydedilemedi');

      setConfigured(items.length > 0);
      showToast('success', 'Yetkiler kaydedildi');
    } catch (err) {
      showToast('error', err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const activeTab = catalog.find((t) => t.key === tab);

  // Sekme rozetinde "kaç detayda yetki verilmiş" sayısı — yönetici hangi sekmeye dokunduğunu görsün.
  const grantedCount = (t) => (t.details || []).filter((d) => ruleOf(d.key).mode !== MODE.NONE).length;

  const actions = canEdit ? (
    <button className="dps-btn primary" onClick={persist} disabled={saving || !targetId}>
      <Icon name="check" size={16} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
    </button>
  ) : null;

  return (
    <DetailShell
      crumb={[{ label: 'Kampanya' }, { label: 'Kampanya Yetkileri', current: true }]}
      tabs={(catalog || []).map((t) => {
        // Sekme etiketinde kaç detayda yetki verildiği görünür (DetailShell'in rozet desteği yok) —
        // yönetici hangi sekmeye dokunduğunu sekmeye girmeden görsün.
        const n = targetId ? grantedCount(t) : 0;
        return {
          key: t.key,
          label: n > 0 ? `${t.name} (${n})` : t.name,
          icon: TAB_ICONS[t.key] || 'list',
          title: t.hint,
        };
      })}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      loading={loading}
      modeless
    >
      <div className="dps-form">
        <Section icon="shield" title="Yetki Kriteri" subtitle="Bu yetkiler kime uygulanacak?">
          <div style={S.critSeg}>
            {CRITERIA.map((c) => (
              <button
                key={c.key}
                type="button"
                style={{ ...S.critBtn, ...(targetType === c.key ? S.critBtnOn : {}) }}
                onClick={() => pickCriterion(c.key)}
              >
                <Icon name={c.icon} size={15} /> {c.label}
              </button>
            ))}
          </div>

          <div style={S.picker}>
            {targetType === TARGET.REP && (
              <label style={S.field}>
                <span style={S.fieldLbl}>Temsilci</span>
                <SearchableSelect
                  value={targetId}
                  selectedLabel={targetLabel}
                  onChange={(v, item) => { setTargetId(v || ''); setTargetLabel(item?.label || ''); }}
                  fetchOptions={searchReps}
                  placeholder="Temsilci ara…"
                  pageSize={50}
                />
              </label>
            )}
            {targetType === TARGET.GROUP && (
              <label style={S.field}>
                <span style={S.fieldLbl}>Temsilci Grubu</span>
                <select style={S.select} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                  <option value="">Seçiniz…</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </label>
            )}
            {targetType === TARGET.TYPE && (
              <label style={S.field}>
                <span style={S.fieldLbl}>Temsilci Tipi</span>
                <select style={S.select} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                  <option value="">Seçiniz…</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            )}
          </div>

          {/* Çözüm önceliği görünür olmalı: yönetici gruba kural yazdığında, o gruptaki bir
              temsilcinin KENDİ kaydının bunu ezeceğini bilmeli. */}
          <p style={S.note}>
            <Icon name="info" size={14} />
            Belge kesilirken en özel kayıt kazanır: <b>Tek Temsilci &gt; Temsilci Grubu &gt; Temsilci Tipi</b>.
          </p>

          {targetId && !configured && !loadingTarget && (
            <p style={S.warn}>
              <Icon name="info" size={14} />
              Bu hedefin henüz hiç yetkisi tanımlı değil — <b>denetim uygulanmıyor</b> (bugünkü davranış sürüyor).
              İlk kuralı kaydettiğiniz anda denetim başlar ve <b>tanımlamadığınız her detay “Yetki Yok” sayılır</b>.
            </p>
          )}
        </Section>

        {!targetId ? (
          <Section icon="user" title="Yetkiler" subtitle="Yetkileri görmek için önce bir hedef seçin">
            <div style={S.empty}>
              Yukarıdan bir temsilci, grup ya da tip seçin.
            </div>
          </Section>
        ) : activeTab && (
          <Section icon={TAB_ICONS[activeTab.key] || 'list'} title={activeTab.name} subtitle={activeTab.hint}>
            {loadingTarget ? (
              <div style={S.empty}>Yükleniyor…</div>
            ) : (
              <div style={S.rows}>
                {(activeTab.details || []).map((d) => (
                  <AuthorizationRow
                    key={d.key}
                    detail={d}
                    rule={ruleOf(d.key)}
                    disabled={!canEdit}
                    onMode={(m) => setMode(d.key, m)}
                    onPatch={(f) => patch(d.key, f)}
                  />
                ))}
              </div>
            )}

            <div style={S.legend}>
              <span><i style={{ ...S.dot, background: C.none }} /> Yetki Yok — alan kilitli</span>
              <span><i style={{ ...S.dot, background: C.limited }} /> Sınırlı — üst sınıra kadar</span>
              <span><i style={{ ...S.dot, background: C.full }} /> Tam Yetki — sınırsız</span>
            </div>
          </Section>
        )}
      </div>

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </DetailShell>
  );
}

// ─────────────────────────────────────────────────────────── Yetki satırı

function AuthorizationRow({ detail, rule, disabled, onMode, onPatch }) {
  const limited = rule.mode === MODE.LIMITED;
  const allowed = detail.allowedCapTypes || [CAP.PERCENT];
  const tone = rule.mode === MODE.FULL ? 'full' : limited ? 'limited' : 'none';

  return (
    <div style={{ ...S.row, borderLeft: `4px solid ${C[tone]}` }}>
      <div style={S.rowName}>
        <b>{detail.name}</b>
        <span>{detail.description}</span>
      </div>

      <div style={S.tri}>
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            disabled={disabled}
            onClick={() => onMode(m.key)}
            style={{
              ...S.triBtn,
              ...(rule.mode === m.key ? { background: C[m.tone], color: '#fff' } : {}),
              ...(disabled ? S.triBtnDisabled : {}),
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ ...S.cap, visibility: limited ? 'visible' : 'hidden' }}>
        <span style={S.capLbl}>En fazla</span>
        <span style={S.capBox}>
          <input
            style={S.capInput}
            inputMode="decimal"
            value={rule.capValue ?? ''}
            disabled={disabled}
            onChange={(e) => onPatch({ capValue: e.target.value })}
          />
          {/* Birden çok birim izinliyse (ör. manuel fiyat: % veya ₺) kullanıcı seçer;
              tek birim varsa sabit etiket gösterilir — gereksiz bir dropdown koymayız. */}
          {allowed.length > 1 ? (
            <select
              style={S.capUnitSelect}
              value={rule.capType ?? allowed[0]}
              disabled={disabled}
              onChange={(e) => onPatch({ capType: Number(e.target.value) })}
            >
              {allowed.map((c) => <option key={c} value={c}>{CAP_SUFFIX[c]}</option>)}
            </select>
          ) : (
            <span style={S.capUnit}>{CAP_SUFFIX[allowed[0]]}</span>
          )}
        </span>

        {detail.allowsCapScope && (
          <select
            style={S.scopeSelect}
            value={rule.capScope ?? SCOPE.DOCUMENT}
            disabled={disabled}
            onChange={(e) => onPatch({ capScope: Number(e.target.value) })}
          >
            <option value={SCOPE.DOCUMENT}>belge başına</option>
            <option value={SCOPE.CAMPAIGN}>kampanya başına</option>
          </select>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────── Stiller
// Not: `dps-btn primary` yalnız DetailShell'in .dps kökü içinde doğru renklenir; bu sayfanın
// tüm butonları o ağacın içindedir. Satır stilleri inline — c360/DetailShell'de eşdeğeri yok.

const C = {
  none: '#e07b73',      // soft kırmızı — dikkat çeker ama alarm değildir
  limited: '#b57d18',   // amber
  full: '#3f9d55',      // yeşil
};

const S = {
  critSeg: { display: 'inline-flex', background: '#eef2f6', borderRadius: 11, padding: 4, gap: 3, marginBottom: 14 },
  critBtn: { border: 'none', background: 'transparent', color: '#3c4a5e', padding: '9px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7 },
  critBtnOn: { background: '#16233f', color: '#fff' },

  picker: { maxWidth: 420 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  fieldLbl: { fontSize: 12.5, color: '#3c4a5e', fontWeight: 600 },
  select: { background: '#f2f6f9', border: '1px solid #e9eef3', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: '#18233b', outline: 'none' },

  note: { display: 'flex', alignItems: 'center', gap: 7, margin: '14px 0 0', fontSize: 12.5, color: '#8b96a6' },
  warn: { display: 'flex', alignItems: 'flex-start', gap: 7, margin: '10px 0 0', padding: '10px 14px', fontSize: 12.5, color: '#8a6d1f', background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 10, lineHeight: 1.5 },

  empty: { padding: '28px 4px', color: '#8b96a6', fontSize: 13 },

  rows: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px', borderTop: '1px solid #eef2f6' },
  rowName: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },

  tri: { display: 'inline-flex', background: '#eef2f6', borderRadius: 10, padding: 3, gap: 2, flex: 'none' },
  triBtn: { border: 'none', background: 'transparent', color: '#3c4a5e', padding: '8px 13px', fontSize: 12, borderRadius: 7, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },
  triBtnDisabled: { cursor: 'not-allowed', opacity: 0.6 },

  cap: { display: 'inline-flex', alignItems: 'center', gap: 8, flex: 'none', minWidth: 210, justifyContent: 'flex-end' },
  capLbl: { fontSize: 12, color: C.limited, fontWeight: 600, whiteSpace: 'nowrap' },
  capBox: { display: 'inline-flex', alignItems: 'center', background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 9, overflow: 'hidden' },
  capInput: { width: 54, border: 'none', background: 'transparent', padding: '8px 4px 8px 10px', fontSize: 14, fontWeight: 700, color: C.limited, textAlign: 'right', outline: 'none' },
  capUnit: { padding: '0 11px 0 3px', fontSize: 13, color: C.limited, fontWeight: 700 },
  capUnitSelect: { border: 'none', background: 'transparent', color: C.limited, fontWeight: 700, fontSize: 13, padding: '0 6px', outline: 'none', cursor: 'pointer' },
  scopeSelect: { background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 9, color: C.limited, fontWeight: 600, fontSize: 12, padding: '7px 8px', outline: 'none', cursor: 'pointer' },

  legend: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: '#8b96a6' },
  dot: { display: 'inline-block', width: 11, height: 11, borderRadius: 3, marginRight: 6, verticalAlign: -1 },
};

export default CampaignAuthorizationsPageLegacy;
